import { UserRole } from '@nebalkon-workspace/auth';
import {
  OrderResponse,
  OrderStatuses,
  OrderStatus,
  OrderTypes,
  OrderType,
} from '@nebalkon-workspace/order-api';
import {
  backendCanCompleteOrder,
  hasStaffRole,
  StaffOrderActionId,
  StaffOrderStaffRole,
} from './staff-order-current-flow.model';

/**
 * Cockpit action model: derives supported actions and a single primary next step
 * per staff role from order type, status, return_to_warehouse and prerequisites.
 *
 * Backend lifecycle source: `apps/api/internal/order/model/order_status.go`.
 */

export const StaffOrderOperationalPhaseIds = {
  Canceled: 'canceled',
  Closed: 'closed',
  NewIntake: 'new_intake',
  ManagerProcessing: 'manager_processing',
  Scheduled: 'scheduled',
  TeamAssigned: 'team_assigned',
  OnSiteWork: 'on_site_work',
  TransitToWarehouse: 'transit_to_warehouse',
  TransitToClient: 'transit_to_client',
  WarehouseReceiving: 'warehouse_receiving',
  AtWarehouse: 'at_warehouse',
  AwaitingClientVisit: 'awaiting_client_visit',
  ClientPickupAtWarehouse: 'client_pickup_at_warehouse',
  ReadyToClose: 'ready_to_close',
} as const;

export type StaffOrderOperationalPhaseId =
  (typeof StaffOrderOperationalPhaseIds)[keyof typeof StaffOrderOperationalPhaseIds];

export interface StaffOrderOperationalPhase {
  id: StaffOrderOperationalPhaseId;
  /** ngx-translate key (cockpit redesign namespace). */
  labelKey: string;
}

export type StaffOrderActionKind = 'primary' | 'secondary' | 'destructive';

export type StaffOrderActionBlockReason =
  | 'terminal_order'
  | 'wrong_role'
  | 'wrong_status'
  | 'missing_warehouse'
  | 'pickup_accept_incomplete'
  | 'receive_incomplete'
  | 'awaiting_team_assignment'
  | 'awaiting_mover_start'
  | 'backend_transition_unavailable'
  | 'no_catalog_client'
  | 'client_self_logistics'
  | 'handover_incomplete';

export interface StaffOrderCompositionProgress {
  /** Pickup: mover acceptance before transit (see computeMoverAcceptProgress). */
  moverAccept?: { accepted: number; total: number } | null;
  /** Pickup / delivery+return: warehouse intake progress. */
  warehouseReceive?: { received: number; total: number } | null;
  /** Delivery without return: all order-linked boxes marked delivered
   * (mover POST /delivered or warehouse handover for self pickup).
   * When omitted at in_transit, not operationally ready. */
  deliveryComplete?: boolean;
  /** Self pickup: warehouse handover progress. */
  warehouseHandover?: { handedOver: number; total: number } | null;
}

export interface StaffOrderActionsInput {
  order: Pick<
    OrderResponse,
    | 'type'
    | 'status'
    | 'return_to_warehouse'
    | 'warehouse_id'
    | 'user_id'
    | 'movers'
    | 'client_self_dropoff'
    | 'client_self_pickup'
  >;
  roles: readonly UserRole[];
  progress?: StaffOrderCompositionProgress;
}

export interface StaffOrderSupportedAction {
  id: StaffOrderActionId;
  role: StaffOrderStaffRole;
  kind: StaffOrderActionKind;
  available: boolean;
  blockReason?: StaffOrderActionBlockReason;
  labelKey: string;
  blockedHintKey?: string;
}

export type StaffOrderOperationalScenarioId =
  | 'pickup'
  | 'self_dropoff'
  | 'delivery'
  | 'delivery_return'
  | 'self_pickup'
  | 'moving';

export interface StaffOrderActionGroups {
  primary: StaffOrderSupportedAction[];
  secondary: StaffOrderSupportedAction[];
  destructive: StaffOrderSupportedAction[];
}

export type StaffOrderOperationalBlockerScope = 'readiness' | 'action';

export interface StaffOrderOperationalBlocker {
  code: string;
  scope: StaffOrderOperationalBlockerScope;
  role?: StaffOrderStaffRole;
  actionId?: StaffOrderActionId;
  hintKey?: string;
  message?: string;
  current?: number;
  total?: number;
}

export interface StaffOrderStorageProgress {
  totalUnits: number;
  storedUnits: number;
  deliveredUnits: number;
}

export interface StaffOrderOperationalProgress {
  scenario: StaffOrderOperationalScenarioId;
  currentPhaseStep: number;
  totalPhaseSteps: number;
  phaseCompletionPercent: number;
  moverAccept?: { accepted: number; total: number } | null;
  warehouseReceive?: { received: number; total: number } | null;
  warehouseHandover?: { received: number; total: number } | null;
  deliveryComplete?: boolean;
  storage?: StaffOrderStorageProgress | null;
}

export interface StaffOrderActionsResult {
  operationalPhase: StaffOrderOperationalPhase;
  scenario?: StaffOrderOperationalScenarioId;
  /** Virtual readiness to close (manager POST /complete), independent of DB status. */
  readyToClose: boolean;
  blockers?: StaffOrderOperationalBlocker[];
  progress?: StaffOrderOperationalProgress;
  supportedActions: StaffOrderSupportedAction[];
  byRole: Partial<Record<StaffOrderStaffRole, StaffOrderSupportedAction[]>>;
  actionGroupsByRole?: Partial<
    Record<StaffOrderStaffRole, StaffOrderActionGroups>
  >;
  primaryNextByRole: Partial<
    Record<StaffOrderStaffRole, StaffOrderSupportedAction | null>
  >;
  cockpitActionGroups?: StaffOrderActionGroups | null;
  /** Header CTA: manager orchestration first, then warehouse, then mover. */
  cockpitPrimaryAction: StaffOrderSupportedAction | null;
}

type OrderSlice = StaffOrderActionsInput['order'];

const COCKPIT_ROLE_PRIORITY: readonly StaffOrderStaffRole[] = [
  'manager',
  'warehouse_worker',
  'mover',
];

const PHASE_ORDER_BY_SCENARIO: Record<
  StaffOrderOperationalScenarioId,
  readonly StaffOrderOperationalPhaseId[]
> = {
  pickup: [
    StaffOrderOperationalPhaseIds.NewIntake,
    StaffOrderOperationalPhaseIds.ManagerProcessing,
    StaffOrderOperationalPhaseIds.Scheduled,
    StaffOrderOperationalPhaseIds.TeamAssigned,
    StaffOrderOperationalPhaseIds.OnSiteWork,
    StaffOrderOperationalPhaseIds.TransitToWarehouse,
    StaffOrderOperationalPhaseIds.WarehouseReceiving,
    StaffOrderOperationalPhaseIds.AtWarehouse,
    StaffOrderOperationalPhaseIds.ReadyToClose,
    StaffOrderOperationalPhaseIds.Closed,
  ],
  self_dropoff: [
    StaffOrderOperationalPhaseIds.NewIntake,
    StaffOrderOperationalPhaseIds.ManagerProcessing,
    StaffOrderOperationalPhaseIds.AwaitingClientVisit,
    StaffOrderOperationalPhaseIds.TransitToWarehouse,
    StaffOrderOperationalPhaseIds.WarehouseReceiving,
    StaffOrderOperationalPhaseIds.AtWarehouse,
    StaffOrderOperationalPhaseIds.ReadyToClose,
    StaffOrderOperationalPhaseIds.Closed,
  ],
  delivery: [
    StaffOrderOperationalPhaseIds.NewIntake,
    StaffOrderOperationalPhaseIds.ManagerProcessing,
    StaffOrderOperationalPhaseIds.Scheduled,
    StaffOrderOperationalPhaseIds.TeamAssigned,
    StaffOrderOperationalPhaseIds.OnSiteWork,
    StaffOrderOperationalPhaseIds.TransitToClient,
    StaffOrderOperationalPhaseIds.ReadyToClose,
    StaffOrderOperationalPhaseIds.Closed,
  ],
  delivery_return: [
    StaffOrderOperationalPhaseIds.NewIntake,
    StaffOrderOperationalPhaseIds.ManagerProcessing,
    StaffOrderOperationalPhaseIds.Scheduled,
    StaffOrderOperationalPhaseIds.TeamAssigned,
    StaffOrderOperationalPhaseIds.OnSiteWork,
    StaffOrderOperationalPhaseIds.TransitToWarehouse,
    StaffOrderOperationalPhaseIds.WarehouseReceiving,
    StaffOrderOperationalPhaseIds.AtWarehouse,
    StaffOrderOperationalPhaseIds.ReadyToClose,
    StaffOrderOperationalPhaseIds.Closed,
  ],
  self_pickup: [
    StaffOrderOperationalPhaseIds.NewIntake,
    StaffOrderOperationalPhaseIds.ManagerProcessing,
    StaffOrderOperationalPhaseIds.AwaitingClientVisit,
    StaffOrderOperationalPhaseIds.AtWarehouse,
    StaffOrderOperationalPhaseIds.ClientPickupAtWarehouse,
    StaffOrderOperationalPhaseIds.ReadyToClose,
    StaffOrderOperationalPhaseIds.Closed,
  ],
  moving: [
    StaffOrderOperationalPhaseIds.NewIntake,
    StaffOrderOperationalPhaseIds.ManagerProcessing,
    StaffOrderOperationalPhaseIds.Scheduled,
    StaffOrderOperationalPhaseIds.TeamAssigned,
    StaffOrderOperationalPhaseIds.OnSiteWork,
    StaffOrderOperationalPhaseIds.ReadyToClose,
    StaffOrderOperationalPhaseIds.Closed,
  ],
};

const ACTION_LABEL_KEYS: Record<StaffOrderActionId, string> = {
  take_in_progress: 'staff.orders.cockpit.actions.takeInProgress',
  agree_pending: 'staff.orders.cockpit.actions.agreePending',
  assign_movers: 'staff.orders.cockpit.actions.assignMovers',
  cancel: 'staff.orders.cockpit.actions.cancel',
  complete: 'staff.orders.cockpit.actions.complete',
  confirm_client_visit: 'staff.orders.cockpit.actions.confirmClientVisit',
  confirm_payment: 'staff.orders.cockpit.actions.confirmPayment',
  edit_order: 'staff.orders.cockpit.actions.editOrder',
  add_note: 'staff.orders.cockpit.actions.addNote',
  manage_order_items: 'staff.orders.cockpit.actions.manageOrderItems',
  mover_start: 'staff.orders.cockpit.actions.moverStart',
  mover_transit: 'staff.orders.cockpit.actions.moverTransit',
  mover_delivered: 'staff.orders.cockpit.actions.moverDelivered',
  mover_accept_box: 'staff.orders.cockpit.actions.moverAcceptBox',
  mover_accept_item: 'staff.orders.cockpit.actions.moverAcceptItem',
  mover_inventory: 'staff.orders.cockpit.actions.moverInventory',
  release_items_to_client: 'staff.orders.cockpit.actions.releaseItemsToClient',
  warehouse_receive: 'staff.orders.cockpit.actions.warehouseReceive',
  warehouse_handover: 'staff.orders.cockpit.actions.warehouseHandover',
};

const PHASE_LABEL_KEYS: Record<StaffOrderOperationalPhaseId, string> = {
  [StaffOrderOperationalPhaseIds.Canceled]:
    'staff.orders.cockpit.phases.canceled',
  [StaffOrderOperationalPhaseIds.Closed]: 'staff.orders.cockpit.phases.closed',
  [StaffOrderOperationalPhaseIds.NewIntake]:
    'staff.orders.cockpit.phases.newIntake',
  [StaffOrderOperationalPhaseIds.ManagerProcessing]:
    'staff.orders.cockpit.phases.managerProcessing',
  [StaffOrderOperationalPhaseIds.Scheduled]:
    'staff.orders.cockpit.phases.scheduled',
  [StaffOrderOperationalPhaseIds.TeamAssigned]:
    'staff.orders.cockpit.phases.teamAssigned',
  [StaffOrderOperationalPhaseIds.OnSiteWork]:
    'staff.orders.cockpit.phases.onSiteWork',
  [StaffOrderOperationalPhaseIds.TransitToWarehouse]:
    'staff.orders.cockpit.phases.transitToWarehouse',
  [StaffOrderOperationalPhaseIds.TransitToClient]:
    'staff.orders.cockpit.phases.transitToClient',
  [StaffOrderOperationalPhaseIds.WarehouseReceiving]:
    'staff.orders.cockpit.phases.warehouseReceiving',
  [StaffOrderOperationalPhaseIds.AtWarehouse]:
    'staff.orders.cockpit.phases.atWarehouse',
  [StaffOrderOperationalPhaseIds.AwaitingClientVisit]:
    'staff.orders.cockpit.phases.awaitingClientVisit',
  [StaffOrderOperationalPhaseIds.ClientPickupAtWarehouse]:
    'staff.orders.cockpit.phases.clientPickupAtWarehouse',
  [StaffOrderOperationalPhaseIds.ReadyToClose]:
    'staff.orders.cockpit.phases.readyToClose',
};

function orderType(order: OrderSlice): OrderType {
  return order.type as OrderType;
}

function orderStatus(order: OrderSlice): OrderStatus {
  return order.status as OrderStatus;
}

function isTerminalStatus(status: OrderStatus): boolean {
  return (
    status === OrderStatuses.Completed || status === OrderStatuses.Canceled
  );
}

function hasAssignedMovers(order: OrderSlice): boolean {
  return (order.movers?.length ?? 0) > 0;
}

function isClientSelfLogistics(order: OrderSlice): boolean {
  return (
    order.client_self_dropoff === true || order.client_self_pickup === true
  );
}

function isClientSelfDropoff(order: OrderSlice): boolean {
  return order.client_self_dropoff === true;
}

function isClientSelfPickup(order: OrderSlice): boolean {
  return order.client_self_pickup === true;
}

function warehouseHandoverComplete(
  progress: StaffOrderCompositionProgress | undefined,
): boolean {
  const handover = progress?.warehouseHandover;
  if (!handover || handover.total === 0) {
    return progress?.deliveryComplete === true;
  }
  return handover.handedOver >= handover.total;
}

function moverAcceptComplete(
  progress: StaffOrderCompositionProgress | undefined,
): boolean {
  const accept = progress?.moverAccept;
  if (!accept || accept.total === 0) {
    return false;
  }
  return accept.accepted >= accept.total;
}

function warehouseReceiveComplete(
  progress: StaffOrderCompositionProgress | undefined,
): boolean {
  const receive = progress?.warehouseReceive;
  if (!receive || receive.total === 0) {
    return true;
  }
  return receive.received >= receive.total;
}

function warehouseReceivePartial(
  progress: StaffOrderCompositionProgress | undefined,
): boolean {
  const receive = progress?.warehouseReceive;
  if (!receive || receive.total === 0) {
    return false;
  }
  return receive.received > 0 && receive.received < receive.total;
}

function makeAction(
  id: StaffOrderActionId,
  role: StaffOrderStaffRole,
  kind: StaffOrderActionKind,
  available: boolean,
  blockReason?: StaffOrderActionBlockReason,
  blockedHintKey?: string,
): StaffOrderSupportedAction {
  return {
    id,
    role,
    kind,
    available,
    blockReason,
    labelKey: ACTION_LABEL_KEYS[id],
    blockedHintKey,
  };
}

/** Virtual operational phase for cockpit header (not a DB status). */
export function computeStaffOrderOperationalPhase(
  order: OrderSlice,
  progress?: StaffOrderCompositionProgress,
): StaffOrderOperationalPhase {
  const status = orderStatus(order);
  const type = orderType(order);
  const returnToWarehouse = order.return_to_warehouse;

  if (status === OrderStatuses.Canceled) {
    return phase(StaffOrderOperationalPhaseIds.Canceled);
  }
  if (status === OrderStatuses.Completed) {
    return phase(StaffOrderOperationalPhaseIds.Closed);
  }

  if (isStaffOrderReadyToClose(order, progress)) {
    return phase(StaffOrderOperationalPhaseIds.ReadyToClose);
  }

  switch (status) {
    case OrderStatuses.New:
      return phase(StaffOrderOperationalPhaseIds.NewIntake);
    case OrderStatuses.InProcess:
      return phase(StaffOrderOperationalPhaseIds.ManagerProcessing);
    case OrderStatuses.Pending:
      if (isClientSelfLogistics(order)) {
        return phase(StaffOrderOperationalPhaseIds.AwaitingClientVisit);
      }
      return hasAssignedMovers(order)
        ? phase(StaffOrderOperationalPhaseIds.TeamAssigned)
        : phase(StaffOrderOperationalPhaseIds.Scheduled);
    case OrderStatuses.InWork:
      return phase(StaffOrderOperationalPhaseIds.OnSiteWork);
    case OrderStatuses.Stored:
      return phase(StaffOrderOperationalPhaseIds.AtWarehouse);
    case OrderStatuses.InTransit: {
      if (isClientSelfPickup(order)) {
        return phase(StaffOrderOperationalPhaseIds.ClientPickupAtWarehouse);
      }
      if (type === OrderTypes.Pickup || isClientSelfDropoff(order)) {
        return warehouseReceivePartial(progress)
          ? phase(StaffOrderOperationalPhaseIds.WarehouseReceiving)
          : phase(StaffOrderOperationalPhaseIds.TransitToWarehouse);
      }
      if (type === OrderTypes.Delivery) {
        if (returnToWarehouse) {
          return warehouseReceivePartial(progress)
            ? phase(StaffOrderOperationalPhaseIds.WarehouseReceiving)
            : phase(StaffOrderOperationalPhaseIds.TransitToWarehouse);
        }
        return phase(StaffOrderOperationalPhaseIds.TransitToClient);
      }
      return phase(StaffOrderOperationalPhaseIds.TransitToWarehouse);
    }
    default:
      return phase(StaffOrderOperationalPhaseIds.OnSiteWork);
  }
}

function phase(id: StaffOrderOperationalPhaseId): StaffOrderOperationalPhase {
  return { id, labelKey: PHASE_LABEL_KEYS[id] };
}

function computeOperationalScenario(
  order: OrderSlice,
): StaffOrderOperationalScenarioId {
  if (orderType(order) === OrderTypes.Moving) {
    return 'moving';
  }
  if (orderType(order) === OrderTypes.Pickup && isClientSelfDropoff(order)) {
    return 'self_dropoff';
  }
  if (orderType(order) === OrderTypes.Pickup) {
    return 'pickup';
  }
  if (orderType(order) === OrderTypes.Delivery && isClientSelfPickup(order)) {
    return 'self_pickup';
  }
  if (orderType(order) === OrderTypes.Delivery && order.return_to_warehouse) {
    return 'delivery_return';
  }
  return 'delivery';
}

function computeOperationalProgress(
  scenario: StaffOrderOperationalScenarioId,
  phaseId: StaffOrderOperationalPhaseId,
  progress?: StaffOrderCompositionProgress,
): StaffOrderOperationalProgress {
  const phaseOrder = PHASE_ORDER_BY_SCENARIO[scenario];
  const index = phaseOrder.indexOf(phaseId);
  const totalPhaseSteps = phaseOrder.length;
  const currentPhaseStep =
    phaseId === StaffOrderOperationalPhaseIds.Canceled
      ? totalPhaseSteps
      : index >= 0
        ? index + 1
        : 1;

  return {
    scenario,
    currentPhaseStep,
    totalPhaseSteps,
    phaseCompletionPercent:
      totalPhaseSteps > 0
        ? Math.floor((currentPhaseStep * 100) / totalPhaseSteps)
        : 0,
    moverAccept: progress?.moverAccept ?? null,
    warehouseReceive: progress?.warehouseReceive ?? null,
    warehouseHandover: progress?.warehouseHandover
      ? {
          received: progress.warehouseHandover.handedOver,
          total: progress.warehouseHandover.total,
        }
      : null,
    deliveryComplete: progress?.deliveryComplete,
    storage: null,
  };
}

/**
 * Virtual «ready to close»: operational work finished; manager may POST /complete.
 * Pickup / delivery+return: stored + full warehouse intake when progress known.
 * Delivery full return: in_transit (manager closes after mover delivery).
 * Moving: in_work (no warehouse leg).
 */
export function isStaffOrderReadyToClose(
  order: OrderSlice,
  progress?: StaffOrderCompositionProgress,
): boolean {
  if (isTerminalStatus(orderStatus(order))) {
    return false;
  }

  if (!backendCanCompleteOrder(order)) {
    return false;
  }

  const type = orderType(order);

  switch (type) {
    case OrderTypes.Pickup:
    case OrderTypes.Delivery:
      if (order.return_to_warehouse && isClientSelfPickup(order)) {
        return (
          orderStatus(order) === OrderStatuses.Stored &&
          warehouseHandoverComplete(progress)
        );
      }
      if (order.return_to_warehouse || type === OrderTypes.Pickup) {
        return (
          orderStatus(order) === OrderStatuses.Stored &&
          warehouseReceiveComplete(progress)
        );
      }
      return (
        orderStatus(order) === OrderStatuses.InTransit &&
        progress?.deliveryComplete === true
      );
    case OrderTypes.Moving:
      return orderStatus(order) === OrderStatuses.InWork;
    default:
      return false;
  }
}

function buildManagerActions(
  order: OrderSlice,
  progress: StaffOrderCompositionProgress | undefined,
  roles: readonly UserRole[],
): StaffOrderSupportedAction[] {
  if (!hasStaffRole(roles, 'manager')) {
    return [];
  }

  const status = orderStatus(order);
  const actions: StaffOrderSupportedAction[] = [];

  if (isTerminalStatus(status)) {
    return actions;
  }

  const ready = isStaffOrderReadyToClose(order, progress);
  const canCompleteBackend = backendCanCompleteOrder(order);

  if (status === OrderStatuses.New) {
    actions.push(makeAction('take_in_progress', 'manager', 'primary', true));
  } else if (status === OrderStatuses.InProcess) {
    actions.push(makeAction('agree_pending', 'manager', 'primary', true));
  } else if (status === OrderStatuses.Pending) {
    if (isClientSelfLogistics(order)) {
      actions.push(
        makeAction('confirm_client_visit', 'manager', 'primary', true),
      );
      actions.push(
        makeAction(
          'assign_movers',
          'manager',
          'secondary',
          false,
          'client_self_logistics',
          'staff.orders.cockpit.hints.clientSelfLogistics',
        ),
      );
    } else if (!hasAssignedMovers(order)) {
      actions.push(makeAction('assign_movers', 'manager', 'primary', true));
    } else {
      actions.push(
        makeAction(
          'assign_movers',
          'manager',
          'secondary',
          false,
          'awaiting_mover_start',
          'staff.orders.cockpit.hints.awaitingMoverStart',
        ),
      );
    }
  } else if (
    status === OrderStatuses.InWork ||
    status === OrderStatuses.InTransit
  ) {
    if (!isClientSelfLogistics(order)) {
      actions.push(makeAction('assign_movers', 'manager', 'secondary', true));
    }
  }

  if (ready && canCompleteBackend) {
    actions.push(makeAction('complete', 'manager', 'primary', true));
  } else if (canCompleteBackend && !ready) {
    const incompleteReceive =
      orderType(order) === OrderTypes.Pickup ||
      (orderType(order) === OrderTypes.Delivery &&
        order.return_to_warehouse &&
        !isClientSelfPickup(order));
    const awaitingDelivery =
      orderType(order) === OrderTypes.Delivery &&
      !order.return_to_warehouse &&
      orderStatus(order) === OrderStatuses.InTransit &&
      progress?.deliveryComplete !== true;
    const awaitingHandover =
      isClientSelfPickup(order) &&
      !warehouseHandoverComplete(progress) &&
      (orderStatus(order) === OrderStatuses.InTransit ||
        (order.return_to_warehouse &&
          orderStatus(order) === OrderStatuses.Stored));

    let blockReason: StaffOrderActionBlockReason;
    let hint: string | undefined;
    if (incompleteReceive) {
      blockReason = 'receive_incomplete';
      hint = 'staff.orders.cockpit.hints.receiveIncomplete';
    } else if (
      awaitingHandover ||
      (awaitingDelivery && isClientSelfPickup(order))
    ) {
      blockReason = 'handover_incomplete';
      hint = 'staff.orders.cockpit.hints.handoverIncomplete';
    } else if (awaitingDelivery) {
      blockReason = 'awaiting_mover_start';
      hint = 'staff.orders.cockpit.hints.awaitingDelivery';
    } else {
      blockReason = 'backend_transition_unavailable';
    }

    actions.push(
      makeAction('complete', 'manager', 'secondary', false, blockReason, hint),
    );
  }

  if (
    status === OrderStatuses.New ||
    status === OrderStatuses.InProcess ||
    status === OrderStatuses.Pending
  ) {
    actions.push(makeAction('cancel', 'manager', 'destructive', true));
  }

  actions.push(makeAction('edit_order', 'manager', 'secondary', true));
  actions.push(makeAction('add_note', 'manager', 'secondary', true));

  if (order.user_id) {
    actions.push(
      makeAction('manage_order_items', 'manager', 'secondary', true),
    );
  } else {
    actions.push(
      makeAction(
        'manage_order_items',
        'manager',
        'secondary',
        false,
        'no_catalog_client',
      ),
    );
  }

  return actions;
}

function buildMoverActions(
  order: OrderSlice,
  progress: StaffOrderCompositionProgress | undefined,
  roles: readonly UserRole[],
): StaffOrderSupportedAction[] {
  if (!hasStaffRole(roles, 'mover')) {
    return [];
  }

  const status = orderStatus(order);
  const type = orderType(order);
  const returnToWarehouse = order.return_to_warehouse;

  if (isTerminalStatus(status)) {
    return [];
  }

  if (isClientSelfLogistics(order)) {
    return [];
  }

  const actions: StaffOrderSupportedAction[] = [];

  if (status === OrderStatuses.Pending) {
    actions.push(makeAction('mover_start', 'mover', 'primary', true));
    return actions;
  }

  const inventoryEligible =
    !!order.user_id &&
    (status === OrderStatuses.InWork || status === OrderStatuses.InTransit) &&
    (type === OrderTypes.Pickup ||
      type === OrderTypes.Delivery ||
      type === OrderTypes.Moving);

  if (inventoryEligible) {
    actions.push(makeAction('mover_inventory', 'mover', 'secondary', true));
  }

  if (type === OrderTypes.Pickup && status === OrderStatuses.InWork) {
    const acceptDone = moverAcceptComplete(progress);
    if (!acceptDone) {
      actions.push(makeAction('mover_accept_box', 'mover', 'primary', true));
      actions.push(makeAction('mover_accept_item', 'mover', 'primary', true));
      actions.push(
        makeAction(
          'mover_transit',
          'mover',
          'secondary',
          false,
          'pickup_accept_incomplete',
          'staff.orders.cockpit.hints.pickupAcceptIncomplete',
        ),
      );
    } else {
      actions.push(makeAction('mover_transit', 'mover', 'primary', true));
    }
    return actions;
  }

  if (type === OrderTypes.Moving && status === OrderStatuses.InWork) {
    return actions;
  }

  if (type === OrderTypes.Delivery && returnToWarehouse) {
    if (status === OrderStatuses.InWork || status === OrderStatuses.InTransit) {
      actions.push(
        makeAction('release_items_to_client', 'mover', 'secondary', true),
      );
    }
    if (status === OrderStatuses.InWork) {
      actions.push(makeAction('mover_transit', 'mover', 'primary', true));
    } else if (status === OrderStatuses.InTransit) {
      actions.push(makeAction('mover_transit', 'mover', 'primary', true));
    }
    return actions;
  }

  if (type === OrderTypes.Delivery && !returnToWarehouse) {
    if (status === OrderStatuses.InWork) {
      actions.push(makeAction('mover_transit', 'mover', 'primary', true));
    } else if (status === OrderStatuses.InTransit) {
      actions.push(makeAction('mover_delivered', 'mover', 'primary', true));
    }
    return actions;
  }

  return actions;
}

function buildWarehouseActions(
  order: OrderSlice,
  progress: StaffOrderCompositionProgress | undefined,
  roles: readonly UserRole[],
): StaffOrderSupportedAction[] {
  if (!hasStaffRole(roles, 'warehouse_worker')) {
    return [];
  }

  const status = orderStatus(order);
  const type = orderType(order);

  if (isTerminalStatus(status)) {
    return [];
  }

  if (isTerminalStatus(status)) {
    return [];
  }

  if (isClientSelfPickup(order) && status === OrderStatuses.InTransit) {
    return buildWarehouseHandoverActions(order, progress);
  }

  const intakeApplies =
    status === OrderStatuses.InTransit &&
    (type === OrderTypes.Pickup ||
      (type === OrderTypes.Delivery && order.return_to_warehouse));

  if (!intakeApplies) {
    return [];
  }

  if (!order.warehouse_id) {
    return [
      makeAction(
        'warehouse_receive',
        'warehouse_worker',
        'primary',
        false,
        'missing_warehouse',
        'staff.orders.cockpit.hints.missingWarehouse',
      ),
    ];
  }

  const receiveDone = warehouseReceiveComplete(progress);
  const receiveProgressKnown =
    (progress?.warehouseReceive?.total ?? 0) > 0;
  if (receiveDone && orderStatus(order) === OrderStatuses.Stored) {
    return [];
  }

  if (receiveDone && receiveProgressKnown) {
    return [
      makeAction('complete', 'warehouse_worker', 'primary', true),
    ];
  }

  return [
    makeAction(
      'warehouse_receive',
      'warehouse_worker',
      'primary',
      !receiveDone,
      receiveDone ? 'backend_transition_unavailable' : undefined,
      receiveDone
        ? 'staff.orders.cockpit.hints.warehouseReceiveComplete'
        : undefined,
    ),
  ];
}

function buildWarehouseHandoverActions(
  order: OrderSlice,
  progress: StaffOrderCompositionProgress | undefined,
): StaffOrderSupportedAction[] {
  if (!order.warehouse_id) {
    return [
      makeAction(
        'warehouse_handover',
        'warehouse_worker',
        'primary',
        false,
        'missing_warehouse',
        'staff.orders.cockpit.hints.missingWarehouse',
      ),
    ];
  }

  const handoverDone = warehouseHandoverComplete(progress);
  return [
    makeAction(
      'warehouse_handover',
      'warehouse_worker',
      'primary',
      !handoverDone,
      handoverDone ? 'backend_transition_unavailable' : undefined,
      handoverDone
        ? 'staff.orders.cockpit.hints.warehouseHandoverComplete'
        : undefined,
    ),
  ];
}

function pickPrimaryAction(
  actions: StaffOrderSupportedAction[],
): StaffOrderSupportedAction | null {
  return (
    actions.find((a) => a.kind === 'primary' && a.available) ??
    actions.find((a) => a.kind === 'primary') ??
    null
  );
}

function mergeActionsForRoles(
  ...groups: StaffOrderSupportedAction[][]
): StaffOrderSupportedAction[] {
  const seen = new Set<string>();
  const merged: StaffOrderSupportedAction[] = [];
  for (const group of groups) {
    for (const action of group) {
      const key = `${action.role}:${action.id}:${action.kind}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      merged.push(action);
    }
  }
  return merged;
}

function groupActionsByKind(
  actions: StaffOrderSupportedAction[],
): StaffOrderActionGroups {
  return {
    primary: actions.filter((action) => action.kind === 'primary'),
    secondary: actions.filter((action) => action.kind === 'secondary'),
    destructive: actions.filter((action) => action.kind === 'destructive'),
  };
}

function buildActionGroupsByRole(
  byRole: StaffOrderActionsResult['byRole'],
): StaffOrderActionsResult['actionGroupsByRole'] {
  return Object.fromEntries(
    Object.entries(byRole).map(([role, actions]) => [
      role,
      groupActionsByKind(actions ?? []),
    ]),
  ) as StaffOrderActionsResult['actionGroupsByRole'];
}

function buildOperationalBlockers(
  byRole: StaffOrderActionsResult['byRole'],
): StaffOrderOperationalBlocker[] {
  const blockers: StaffOrderOperationalBlocker[] = [];
  for (const [role, actions] of Object.entries(byRole) as [
    StaffOrderStaffRole,
    StaffOrderSupportedAction[],
  ][]) {
    const primary = pickPrimaryAction(actions);
    if (!primary || primary.available || !primary.blockReason) {
      continue;
    }
    blockers.push({
      code: primary.blockReason,
      scope: 'action',
      role,
      actionId: primary.id,
      hintKey: primary.blockedHintKey,
    });
  }
  return blockers;
}

/** Main entry: supported actions and next-step model for staff order cockpit. */
export function supportedOrderActions(
  input: StaffOrderActionsInput,
): StaffOrderActionsResult {
  const { order, roles, progress } = input;

  const managerActions = buildManagerActions(order, progress, roles);
  const moverActions = buildMoverActions(order, progress, roles);
  const warehouseActions = buildWarehouseActions(order, progress, roles);

  const byRole: Partial<
    Record<StaffOrderStaffRole, StaffOrderSupportedAction[]>
  > = {};
  if (managerActions.length) {
    byRole.manager = managerActions;
  }
  if (moverActions.length) {
    byRole.mover = moverActions;
  }
  if (warehouseActions.length) {
    byRole.warehouse_worker = warehouseActions;
  }

  const primaryNextByRole: Partial<
    Record<StaffOrderStaffRole, StaffOrderSupportedAction | null>
  > = {};
  for (const role of ['manager', 'mover', 'warehouse_worker'] as const) {
    const roleActions = byRole[role];
    if (roleActions) {
      primaryNextByRole[role] = pickPrimaryAction(roleActions);
    }
  }

  let cockpitPrimaryAction: StaffOrderSupportedAction | null = null;
  let cockpitActionGroups: StaffOrderActionGroups | null = null;
  const actionGroupsByRole = buildActionGroupsByRole(byRole);
  for (const role of COCKPIT_ROLE_PRIORITY) {
    if (!hasStaffRole(roles, role)) {
      continue;
    }
    const primary = primaryNextByRole[role];
    if (primary?.available) {
      cockpitPrimaryAction = primary;
      cockpitActionGroups = actionGroupsByRole?.[role] ?? null;
      break;
    }
    if (!cockpitActionGroups) {
      cockpitActionGroups = actionGroupsByRole?.[role] ?? null;
    }
  }

  const operationalPhase = computeStaffOrderOperationalPhase(order, progress);
  const scenario = computeOperationalScenario(order);

  return {
    scenario,
    operationalPhase,
    readyToClose: isStaffOrderReadyToClose(order, progress),
    blockers: buildOperationalBlockers(byRole),
    progress: computeOperationalProgress(
      scenario,
      operationalPhase.id,
      progress,
    ),
    supportedActions: mergeActionsForRoles(
      managerActions,
      moverActions,
      warehouseActions,
    ),
    byRole,
    actionGroupsByRole,
    primaryNextByRole,
    cockpitActionGroups,
    cockpitPrimaryAction,
  };
}
